// Kaydırmalı yapboz doğrulaması (giriş ekranı) — uygulamadan bağımsız.
// Sunucu yapbozu üretir (`load`), bu bileşen gösterir; kullanıcı kaydırıcıyla
// parçayı deliğe getirir, bırakınca konum + sürükleme izi `verify`'a gider.
// Doğruysa `onSolved(passToken)` — giriş isteği bu belgeyle tekrarlanır.
// Fare, dokunmatik (pointer olayları) ve klavye (← → ile kaydır, Enter ile
// onayla) desteklenir. Backend: modules/security/loginPuzzle.ts.

import { KeyboardEvent, PointerEvent, useCallback, useEffect, useRef, useState } from "react";
import "./sliderPuzzle.css";

export interface SliderPuzzleChallenge {
  id: string;
  width: number;
  height: number;
  pieceSize: number;
  pieceY: number;
  background: string;
  piece: string;
}

export interface SliderPuzzleTrackPoint {
  x: number;
  t: number;
}

export interface SliderPuzzleStrings {
  title: string;
  instruction: string;
  refresh: string;
  verifying: string;
  success: string;
  failed: string;
  loadFailed: string;
  sliderLabel: string;
  cancel: string;
}

export const DEFAULT_SLIDER_PUZZLE_STRINGS: SliderPuzzleStrings = {
  title: "Güvenlik doğrulaması",
  instruction: "Devam etmek için parçayı kaydırarak boşluğa yerleştirin.",
  refresh: "Yeni yapboz",
  verifying: "Kontrol ediliyor…",
  success: "Doğrulandı",
  failed: "Parça yerine oturmadı, tekrar deneyin.",
  loadFailed: "Doğrulama yüklenemedi.",
  sliderLabel: "Yapboz parçasını kaydır",
  cancel: "Vazgeç",
};

export interface SliderPuzzleProps {
  /** Yeni yapboz (ör. `GET /auth/puzzle`). */
  load: () => Promise<SliderPuzzleChallenge>;
  /** Çözümü doğrula (ör. `POST /auth/puzzle/verify`); başarısızsa hata fırlatmalı. */
  verify: (id: string, x: number, track: SliderPuzzleTrackPoint[]) => Promise<{ passToken: string }>;
  onSolved: (passToken: string) => void;
  onCancel?: () => void;
  /** Sunucu hata mesajını çıkar (ör. axios `response.data.message`). */
  errorMessage?: (err: unknown) => string | undefined;
  strings?: Partial<SliderPuzzleStrings>;
}

type Status = "loading" | "ready" | "verifying" | "failed" | "success" | "error";

export function SliderPuzzle({ load, verify, onSolved, onCancel, errorMessage, strings }: SliderPuzzleProps) {
  const s = { ...DEFAULT_SLIDER_PUZZLE_STRINGS, ...strings };
  const [challenge, setChallenge] = useState<SliderPuzzleChallenge | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState<string | null>(null);
  const [x, setX] = useState(0);
  const dragging = useRef(false);
  const startClientX = useRef(0);
  const startedAt = useRef(0);
  const track = useRef<SliderPuzzleTrackPoint[]>([]);
  const stageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // `load` çoğu zaman satır içi bir fonksiyondur (her çizimde yeni kimlik). Yapboz
  // yalnızca ilk açılışta ve ↻ ile yüklenmeli — üst bileşen yeniden çizildi diye
  // yeni yapboz istenirse görsel "göz kırpar" ve sürüklenen çözüm geçersiz kalır.
  const loadRef = useRef(load);
  loadRef.current = load;
  const requestSeq = useRef(0);

  const reload = useCallback(async () => {
    const seq = ++requestSeq.current;
    setStatus("loading");
    setMessage(null);
    setX(0);
    track.current = [];
    try {
      const next = await loadRef.current();
      if (seq !== requestSeq.current) return; // daha yeni bir istek var
      setChallenge(next);
      setStatus("ready");
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setMessage(errorMessage?.(err) ?? s.loadFailed);
      setStatus("error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Görsel kabın genişliğine sığdırılır (dar ekranda küçülür, geniş formda
  // büyür); sunucuya giden konum her zaman görselin kendi pikseli.
  useEffect(() => {
    const el = stageRef.current;
    if (!el || !challenge) return;
    const update = () => setScale(el.clientWidth / challenge.width || 1);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [challenge]);

  const maxX = challenge ? challenge.width - challenge.pieceSize : 0;
  const clamp = (v: number) => Math.max(0, Math.min(maxX, v));

  const record = (value: number) => {
    if (track.current.length === 0) startedAt.current = performance.now();
    track.current.push({ x: Math.round(value * 10) / 10, t: Math.round(performance.now() - startedAt.current) });
  };

  const submit = async (finalX: number) => {
    if (!challenge) return;
    setStatus("verifying");
    setMessage(null);
    record(finalX);
    try {
      const { passToken } = await verify(challenge.id, Math.round(finalX), track.current);
      setStatus("success");
      onSolved(passToken);
    } catch (err) {
      setMessage(errorMessage?.(err) ?? s.failed);
      setStatus("failed");
      setTimeout(() => void reload(), 900);
    }
  };

  const busy = status !== "ready";

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (busy) return;
    dragging.current = true;
    startClientX.current = e.clientX - x * scale;
    track.current = [];
    record(x);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const next = clamp((e.clientX - startClientX.current) / scale);
    setX(next);
    record(next);
  };

  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    dragging.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
    const final = clamp((e.clientX - startClientX.current) / scale);
    setX(final);
    if (final > 2) void submit(final);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (busy) return;
    const step = e.shiftKey ? 10 : 2;
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const next = clamp(x + (e.key === "ArrowRight" ? step : -step));
      setX(next);
      record(next);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (x > 2) void submit(x);
    }
  };

  const w = challenge?.width ?? 300;
  const h = challenge?.height ?? 160;
  const size = challenge?.pieceSize ?? 48;

  return (
    <div className="spz">
      <div className="spz-head">
        <strong>{s.title}</strong>
        <button type="button" className="spz-refresh" onClick={() => void reload()} disabled={status === "loading" || status === "verifying"} title={s.refresh} aria-label={s.refresh}>
          ↻
        </button>
      </div>
      <p className="spz-hint">{s.instruction}</p>
      <div ref={stageRef} className="spz-stage" style={{ aspectRatio: `${w} / ${h}` }}>
        {challenge && (
          <>
            <img className="spz-bg" src={challenge.background} alt="" draggable={false} />
            <img
              className="spz-piece"
              src={challenge.piece}
              alt=""
              draggable={false}
              style={{
                width: `${(size / w) * 100}%`,
                left: `${(x / w) * 100}%`,
                top: `${(challenge.pieceY / h) * 100}%`,
              }}
            />
          </>
        )}
        {status === "loading" && <div className="spz-overlay">…</div>}
        {status === "success" && <div className="spz-overlay spz-ok">✓ {s.success}</div>}
      </div>
      <div className="spz-track">
        <div className="spz-fill" style={{ width: `${((x + size / 2) / w) * 100}%` }} />
        <div
          className={`spz-handle${status === "failed" ? " spz-handle--failed" : ""}`}
          style={{ left: `${(x / w) * 100}%`, width: `${(size / w) * 100}%` }}
          role="slider"
          tabIndex={0}
          aria-label={s.sliderLabel}
          aria-valuemin={0}
          aria-valuemax={maxX}
          aria-valuenow={Math.round(x)}
          aria-disabled={busy}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onKeyDown={onKeyDown}
        >
          ⇢
        </div>
      </div>
      <p className={`spz-msg${status === "failed" || status === "error" ? " spz-msg--error" : ""}`} aria-live="polite">
        {status === "verifying" ? s.verifying : message ?? " "}
      </p>
      {onCancel && (
        <button type="button" className="spz-cancel" onClick={onCancel}>
          {s.cancel}
        </button>
      )}
    </div>
  );
}
